import { cloneDeep } from 'lodash';

import { Box } from '@hivebrite/design-system/Box';
import { IconPublication } from '@hivebrite/design-system/Icon';

import EmptyDynamicComponent from 'hb-builder/components/BuilderComponents/shared/common/EmptyDynamicComponent';
import type { PublicationItemAttributes } from 'hb-builder/components/BuilderComponents/page/dynamic/Publication/types';
import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useBuilderConfigContext,
  useComponentContext,
  useComponentsContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import { useFallbackNavigate } from 'hb-react/shared/hooks/useFallbackNavigate';
import { APP_TYPE } from 'hb-react/types/appType';

import { INITIAL_DYNAMIC_ATTRIBUTES } from './constants';
import PublicationLayouts from './PublicationLayouts';

const Publication = () => <div />; // never used

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const { components } = useComponentsContext();
  const {
    entity: { isPublic },
    hivebrite: { ids, networkFont },
  } = useBuilderConfigContext();

  const { dynamicAttributes } = item?.attributes || {};
  const { type } = dynamicAttributes || {};
  const { Section } = components;

  return (
    <Section.RenderBuilder attributes={item?.attributes}>
      {type === 'empty' ? (
        <EmptyDynamicComponent dataTestId="builder-empty-publication" />
      ) : (
        <BuilderThemeProvider viewAs={VIEW_AS.USER}>
          <Box display="block" fontFamily={networkFont}>
            <PublicationLayouts
              app={APP_TYPE.BACKOFFICE}
              dynamicAttributes={dynamicAttributes}
              ids={ids}
              isPublic={isPublic}
              type={type}
            />
          </Box>
        </BuilderThemeProvider>
      )}
    </Section.RenderBuilder>
  );
};

const Render = ({
  item,
}: {
  item: DocumentItem<PublicationItemAttributes>;
}) => {
  const {
    bodyWidth,
    isMobilePreview,
    ids,
    isPublic,
    networkRootUrl,
    whiteLabelUrl,
    workspaceConfig,
  } = useRendererContext();
  const { components } = useComponentsContext();
  const { Section } = components;

  const { attributes } = item;
  const { type } = attributes?.dynamicAttributes || {};

  const navigate = useFallbackNavigate();

  if (!type || type === 'empty') {
    return null;
  }

  return (
    <Section.Render
      isMobilePreview={isMobilePreview}
      item={item}
      bodyWidth={bodyWidth}
      workspaceConfig={workspaceConfig}
    >
      <PublicationLayouts
        app={isPublic ? APP_TYPE.PUBLIC_OFFICE : APP_TYPE.FRONT_OFFICE}
        dynamicAttributes={attributes?.dynamicAttributes}
        ids={ids}
        isPublic={isPublic}
        navigate={navigate}
        networkRootUrl={networkRootUrl}
        type={type}
        whiteLabelUrl={whiteLabelUrl}
      />
    </Section.Render>
  );
};

Publication.componentName = 'Publication';
Publication.componentType = 'section';
Publication.componentLabel = __('Publication');
Publication.componentCategory = 'dynamic';
Publication.componentIcon = () => IconPublication;

Publication.canBeDoubleClicked = true;
Publication.canBeDragged = true;
Publication.canBeListed = true;
Publication.canBeSelected = true;

Publication.item = ({ bodyWidth, components, features }) => {
  const { Section } = components;

  return Section.item({
    features: {
      ...features,
      section: {
        ...features?.section,
        category: Publication.componentCategory,
        dynamicAttributes: cloneDeep(INITIAL_DYNAMIC_ATTRIBUTES),
        hasColGap: true,
        name: Publication.componentName,
        numCols: {
          s: 1,
          m: 0.8333333333333334,
          l: 0.8333333333333334,
          xl: 0.66666666666,
        },
        sectionWidth: bodyWidth,
        type: Publication.componentName,
      },
    },
  });
};

Publication.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Publication.SettingsTabs = defaultTabs;

Publication.RenderBuilder = RenderBuilder;
Publication.Render = Render;

export default Publication;
